/**
 * Digital Workforce auto-setup.
 *
 * Digital Workforce used to be switched on by hand: a user opened the Digital
 * Workforce page and clicked "Click here to launch setup", which ran
 * POST /claude-managed/configure (vault, persona agents, MCP servers, Hermes
 * agent, memory stores). Every agency still on Momentum AMP is a HIVE or paid
 * customer now, so there is no gate left and nobody should have to click: the
 * app runs that same setup itself the first time it finds the signed-in user
 * unconfigured.
 *
 * Callers, one run:
 *   - AppShell fires it right after sign-in, in the background, so the global
 *     chat bar, the Digital Team sidebar and Command Center have agents before
 *     the user ever opens the Digital Workforce page.
 *   - DigitalWorkforcePage awaits it when the page opens, as the visible
 *     fallback (progress state, then error + Retry) when the sign-in run failed
 *     or is still going. The older /claude-agents pages do the same through
 *     ClaudeAgentRequestPage.
 *
 * Callers in one tab share a single in-flight run, and a success is remembered
 * for the signed-in token so later callers skip even the status round-trip.
 * Failures are not remembered: the next caller retries. Overlapping runs from
 * other tabs are serialized server-side by the per-user lock in /configure.
 *
 * A user who only ever uses the mobile app is set up server-side on their
 * first chat turn instead (MyAMP routes/mobile/chat.py), possibly without the
 * NowCerts session token that provisioning the NowCerts MCP server needs. The
 * next web sign-in repairs that here: see needsMcpRepair below.
 */
import { configureDwSetup, getStatus } from '../claudeagents/claudeAgentApi';
import { syncAgencyInstalls } from '../../services/myToolsApi';
import { forgetShared, shareInFlight } from '../../utils/inFlight';

/**
 * Fired on window when a run has actually provisioned the user (not on the
 * already-configured path), so components that loaded the agent list before
 * the run finished (GlobalChatBar, SidebarDigitalTeam) load it again.
 */
export const DW_CONFIGURED_EVENT = 'dwConfigured';

/**
 * already_configured: nothing to do. configured: this run provisioned the
 * user's agents. repaired: the user already had agents but no NowCerts MCP
 * credential, and /configure was re-run to attach it.
 */
export type DwSetupOutcome = 'already_configured' | 'configured' | 'repaired';

const NC_TOKEN_MISSING = 'NC token not found. Please sign in again.';

const authToken = () => localStorage.getItem('authToken') || '';
// /configure provisions the user's NowCerts MCP key through Momentum AMP,
// which needs the NowCerts session token alongside the IG JWT.
const ncToken = () => localStorage.getItem('amsToken') || localStorage.getItem('nc_token') || localStorage.getItem('ncToken') || '';
// "Already set up / repaired for this sign-in" — kept in sessionStorage (per tab,
// cleared when the tab closes), not just memory, so a page refresh doesn't re-check
// /status at startup or re-run the MCP repair on every full load. Keyed by the
// auth token, so a different sign-in checks again.
const CONFIGURED_KEY = 'dwConfiguredForToken';
let configuredForToken: string | null = (() => {
  try { return sessionStorage.getItem(CONFIGURED_KEY); } catch { return null; }
})();

function markConfigured(token: string): void {
  configuredForToken = token;
  try { sessionStorage.setItem(CONFIGURED_KEY, token); } catch { /* memory copy still works */ }
}

/**
 * Forget the remembered "already set up" result, so the next ensureDwConfigured()
 * checks /status again. Call when a screen has just seen the user is NOT set up.
 */
export function forgetDwConfigured(): void {
  configuredForToken = null;
  try { sessionStorage.removeItem(CONFIGURED_KEY); } catch { /* ignore */ }
}

/**
 * Make sure the signed-in user's Digital Workforce is provisioned, running
 * /configure when it is not. Resolves with what happened; rejects with the
 * message to show when setup could not run or failed.
 */
export function ensureDwConfigured(): Promise<DwSetupOutcome> {
  const token = authToken();
  if (token && configuredForToken === token) return Promise.resolve('already_configured');
  return shareInFlight(`dwAutoSetup|${token}`, async () => {
    const status = await getStatus();
    const hasAgents = !!status?.configured;
    // Agents but no NowCerts MCP credential: a user whose first setup ran from
    // the mobile app without the NowCerts session token that provisioning
    // needs. /configure is idempotent and reconciles whatever is missing, so
    // re-run it once per sign-in now that the portal has the token. Never for
    // a BYOK user: a re-run with no api_key would put the company key back
    // over theirs (/configure Step 1).
    const needsMcpRepair = hasAgents && status?.has_mcp_token === false && !status?.is_byok;
    if (hasAgents && !needsMcpRepair) {
      markConfigured(token);
      return 'already_configured';
    }
    const nc = ncToken();
    if (!nc) {
      if (hasAgents) {
        // Nothing to repair with; the agents themselves work.
        markConfigured(token);
        return 'already_configured';
      }
      throw new Error(NC_TOKEN_MISSING);
    }
    await configureDwSetup(nc, true);
    markConfigured(token);
    if (hasAgents) return 'repaired';
    // Agent lists fetched while the user had no agents are stale now.
    forgetShared('dwAgents|');
    // Anything an admin installed for the agency before this user was set up
    // is created by this call, so it is in the next agent list.
    await syncAgencyInstalls();
    window.dispatchEvent(new CustomEvent(DW_CONFIGURED_EVENT));
    return 'configured';
  });
}

/**
 * Sign-in hook: run setup in the background and never throw. Rate-only
 * sessions (sourceTool=Rate) are skipped: Digital Workforce is hidden for them
 * (see isWorkspaceEnabled), and running it would provision agents and a Hermes
 * computer nobody would use.
 */
export function bootstrapDigitalWorkforce(): void {
  if (!authToken()) return;
  if (localStorage.getItem('sourceTool') === 'Rate') return;
  ensureDwConfigured().catch((err: unknown) => {
    // The Digital Workforce page shows the error and offers Retry.
    console.warn('[DigitalWorkforce] background setup did not finish:', err instanceof Error ? err.message : err);
  });
}
